import { useMemo, useState } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  ColumnDef,
  flexRender,
} from '@tanstack/react-table'
import { toast } from 'sonner'
import logger from '@/utils/logger'
import { Modal } from '@/components/composed/Modal/Modal'

interface QueryResultsTableProps {
  data: {
    rows: Record<string, unknown>[]
    rowCount: number
    executionTime?: number
    truncated?: boolean
    message?: string
  } | null
}

/**
 * Format a cell value for display
 */
function formatCellValue(value: unknown): string {
  if (value === null) return 'NULL'
  if (value === undefined) return 'undefined'
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2)
    } catch {
      return '[Object]'
    }
  }
  return String(value)
}

/**
 * Truncated Cell - Shows first 3 lines with ellipsis if content is longer
 */
function TruncatedCell({ value }: { value: unknown }) {
  const formatted = formatCellValue(value)

  if (value === null) {
    return <span className="text-muted italic">NULL</span>
  }

  // Style for 3-line truncation
  const truncateStyle: React.CSSProperties = {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    maxWidth: '300px',
  }

  if (typeof value === 'object' && value !== null) {
    return (
      <code
        className="text-xs bg-surface-2 px-1 py-0.5 rounded whitespace-pre-wrap"
        style={truncateStyle}
      >
        {formatted}
      </code>
    )
  }

  return (
    <span className="font-mono text-sm" style={truncateStyle}>
      {formatted}
    </span>
  )
}

/**
 * Row Detail Modal - Shows row data in a pivoted table (field name | value)
 */
function RowDetailModal({
  row,
  onClose,
}: {
  row: Record<string, unknown> | null
  onClose: () => void
}) {
  if (!row) return null

  const entries = Object.entries(row)

  const handleCopyRow = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(row, null, 2))
      toast.success('Row copied to clipboard')
    } catch {
      toast.error('Failed to copy row')
    }
  }

  return (
    <Modal
      open={!!row}
      onClose={onClose}
      title="Row Details"
      size="xl"
      className="max-w-4xl"
      footer={
        <div className="flex justify-end gap-2">
          <button
            onClick={handleCopyRow}
            className="px-4 py-2 text-sm bg-surface-2 rounded-md hover:bg-surface-3 transition-colors"
          >
            Copy as JSON
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Close
          </button>
        </div>
      }
    >
      <div className="max-h-[60vh] overflow-auto">
        <table className="w-full table-fixed">
          <thead className="bg-surface-2 sticky top-0">
            <tr>
              <th className="px-4 py-2 text-left text-sm font-semibold text-foreground w-48 border-b border-border">
                Field
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold text-foreground border-b border-border">
                Value
              </th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([key, value]) => (
              <tr key={key} className="border-b border-border hover:bg-surface-1">
                <td className="px-4 py-3 text-sm font-mono font-semibold text-primary align-top">
                  {key}
                </td>
                <td className="px-4 py-3 text-sm font-mono whitespace-pre-wrap break-all">
                  {value === null ? (
                    <span className="text-muted italic">NULL</span>
                  ) : typeof value === 'object' ? (
                    <code className="text-xs bg-surface-2 px-2 py-1 rounded block">
                      {JSON.stringify(value, null, 2)}
                    </code>
                  ) : (
                    String(value)
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  )
}

/**
 * Query Results Table Component
 *
 * TanStack Table-based results display with sorting and pagination.
 * Click a row to see full details in a pivoted view.
 *
 * Feature: 055-db-console
 */
export function QueryResultsTable({ data }: QueryResultsTableProps) {
  const [selectedRow, setSelectedRow] = useState<Record<string, unknown> | null>(null)

  // Auto-generate columns from first row
  const columns: ColumnDef<Record<string, unknown>>[] = useMemo(() => {
    if (!data?.rows?.[0]) return []

    return Object.keys(data.rows[0]).map((key) => ({
      accessorKey: key,
      header: key,
      cell: (info) => <TruncatedCell value={info.getValue()} />,
    }))
  }, [data])

  const table = useReactTable({
    data: data?.rows || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 50,
      },
    },
  })

  const handleCopyJSON = async () => {
    if (!data?.rows) return

    const jsonStr = JSON.stringify(data.rows, null, 2)
    try {
      await navigator.clipboard.writeText(jsonStr)
      toast.success('Results copied to clipboard')
      logger.info('QueryResultsTable', 'Results copied to clipboard', {
        rowCount: data.rows.length,
      })
    } catch (err) {
      toast.error('Failed to copy results to clipboard')
      logger.error('QueryResultsTable', 'Failed to copy results to clipboard', {
        error: err,
        rowCount: data.rows.length,
      })
    }
  }

  if (!data) {
    return null
  }

  return (
    <div className="space-y-4">
      {/* Metadata Header */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted">
          {data.rowCount} row{data.rowCount !== 1 ? 's' : ''} returned
          {data.executionTime !== undefined && ` in ${data.executionTime}ms`}
        </div>

        <button
          onClick={handleCopyJSON}
          className="px-3 py-1 text-sm bg-surface-2 rounded-md hover:bg-surface-3 transition-colors"
        >
          Copy as JSON
        </button>
      </div>

      {/* Truncation Warning */}
      {data.truncated && (
        <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-md text-sm text-yellow-600">
          {/* React automatically escapes text content, preventing XSS */}
          ⚠️ {data.message}
        </div>
      )}

      {/* Click hint */}
      {data.rows.length > 0 && (
        <div className="text-xs text-muted">
          Click a row to view full details
        </div>
      )}

      {/* Table */}
      <div className="overflow-auto border border-border rounded-md">
        <table className="w-full">
          <thead className="bg-surface-2 border-b border-border">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-4 py-3 text-left text-sm font-semibold text-foreground cursor-pointer hover:bg-surface-3 transition-colors"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className="flex items-center gap-2">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {{
                        asc: ' ↑',
                        desc: ' ↓',
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="border-b border-border hover:bg-surface-1 transition-colors cursor-pointer"
                onClick={() => setSelectedRow(row.original)}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-4 py-2 text-sm align-top">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data.rowCount > 50 && (
        <div className="flex justify-between items-center">
          <button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="px-4 py-2 bg-surface-2 text-foreground rounded-md hover:bg-surface-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          <span className="text-sm text-muted">
            Page {table.getState().pagination.pageIndex + 1} of{' '}
            {table.getPageCount()}
          </span>
          <button
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="px-4 py-2 bg-surface-2 text-foreground rounded-md hover:bg-surface-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      )}

      {/* Row Detail Modal */}
      <RowDetailModal row={selectedRow} onClose={() => setSelectedRow(null)} />
    </div>
  )
}
